/**
 * Candidates API — CRUD, parse, hire, comments, AI match, applications, import, export.
 */

import { apiFetch, apiFetchRaw } from './core'

export const candidates = {
    list: (params = {}) => {
        const qs = new URLSearchParams(params).toString()
        return apiFetch(`/candidates/${qs ? `?${qs}` : ''}`)
    },
    get: (id) => apiFetch(`/candidates/${id}`),
    // Aggregate endpoint — candidate + applications + comments + unread_count + match + interview_questions
    // in a single round-trip. Replaces the 6-way request storm on detail page mount.
    getBundle: (id, include, vacancyId = null) => {
        const qs = new URLSearchParams()
        if (include) qs.set('include', include)
        if (vacancyId) qs.set('vacancy_id', vacancyId)
        const suffix = qs.toString() ? `?${qs.toString()}` : ''
        return apiFetch(`/candidates/${id}/bundle${suffix}`)
    },
    create: (data) => apiFetch('/candidates/', {
        method: 'POST',
        body: JSON.stringify(data),
        // resume_text triggers a synchronous Gemini parse on the backend
        // (crud.create_candidate). Long resumes take ~40s — без AI-бюджета
        // таймаута клиент рвал запрос на 30s и показывал ложный «Сервер
        // недоступен» (Суперкнопка, вкладка Текст/JSON). HARD GATE 6.
        aiTimeout: !!(data && data.resume_text),
    }),
    // BL Суперкнопка: «Импорт по ссылке» для hh.ru. Парсит резюме через HH API
    // (visibility=everyone) и возвращает {full_name, title, age, area, skills,
    // contact:{email,phone}, photo_url, ...}. Кандидата НЕ создаёт — это делает
    // фронт (CandidateAddModal). HH-запрос ходит во внешний API → aiTimeout (60s+,
    // HARD GATE 6), иначе клиент рвёт на 30s ложным «Сервер недоступен».
    parseResumeByUrl: (url) => apiFetch('/hh/resumes/import-by-url', {
        method: 'POST',
        body: JSON.stringify({ url }),
        aiTimeout: true,
    }),
    update: (id, data) => apiFetch(`/candidates/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    delete: (id) => apiFetch(`/candidates/${id}`, { method: 'DELETE' }),
    parseResume: (id, data) => apiFetch(`/candidates/${id}/parse-resume`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    parseText: (data) => apiFetch('/candidates/parse-text', {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    hire: (id, data = {}) => apiFetch(`/candidates/${id}/hire`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    // Interview notes (вкладка «Результаты собеседований»)
    listInterviewNotes: (id) => apiFetch(`/candidates/${id}/interview-notes`),
    createInterviewNote: (id, data) => apiFetch(`/candidates/${id}/interview-notes`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    updateInterviewNote: (candidateId, noteId, data) => apiFetch(`/candidates/${candidateId}/interview-notes/${noteId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
    }),
    deleteInterviewNote: (candidateId, noteId) => apiFetch(`/candidates/${candidateId}/interview-notes/${noteId}`, {
        method: 'DELETE',
    }),
    getComments: (id) => apiFetch(`/candidates/${id}/comments`),
    addComment: (id, text, replyToId = null, attachmentFileId = null) => apiFetch(`/candidates/${id}/comments`, {
        method: 'POST',
        body: JSON.stringify({
            text,
            reply_to_id: replyToId || null,
            attachment_file_id: attachmentFileId || null,
        }),
    }),
    getUnreadCount: (id) => apiFetch(`/candidates/${id}/comments/unread-count`),
    markCommentsRead: (id) => apiFetch(`/candidates/${id}/comments/mark-read`, { method: 'POST' }),
    getReadReceipts: (id) => apiFetch(`/candidates/${id}/comments/read-receipts`),
    editComment: (candidateId, commentId, text) => apiFetch(`/candidates/${candidateId}/comments/${commentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ text }),
    }),
    deleteComment: (candidateId, commentId) => apiFetch(`/candidates/${candidateId}/comments/${commentId}`, {
        method: 'DELETE',
    }),
    toggleReaction: (candidateId, commentId, emoji) => apiFetch(`/candidates/${candidateId}/comments/${commentId}/reactions`, {
        method: 'POST',
        body: JSON.stringify({ emoji }),
    }),
    // Feedback (hiring scorecard)
    getFeedback: (id) => apiFetch(`/candidates/${id}/feedback`),
    addFeedback: (id, data) => apiFetch(`/candidates/${id}/feedback`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    // Заявка арендатора на человека — «этот наш» (см. CandidateOwnershipClaim.jsx).
    //
    // Тело — ЛИЧНОСТЬ, а НЕ идентификатор. Ручка, адресуемая candidate_id,
    // воспроизводит оракул существования: живой идентификатор отвечал бы одно,
    // выдуманный — другое (backend/app/api/candidates/claims.py, issue #5235).
    // Поэтому сюда нельзя добавлять candidate_id даже «для удобства».
    //
    // Ответ всегда 204 без тела — во всех исходах: нашли, не нашли, уже
    // заявлено, человек чужой. apiFetch отдаёт на 204 null, читать в нём нечего.
    claimCandidate: ({ full_name = null, email = null, phone = null } = {}) =>
        apiFetch('/candidates/claims', {
            method: 'POST',
            body: JSON.stringify({ full_name, email, phone }),
        }),
    releaseCandidateClaim: ({ full_name = null, email = null, phone = null } = {}) =>
        apiFetch('/candidates/claims', {
            method: 'DELETE',
            body: JSON.stringify({ full_name, email, phone }),
        }),
    // Преонбординг — явное действие в карточке (см. PreonboardingAction.jsx)
    getPreonboarding: (id) => apiFetch(`/candidates/${id}/preonboarding`),
    startPreonboarding: (id) => apiFetch(`/candidates/${id}/preonboarding/start`, {
        method: 'POST',
    }),
    getCachedMatchScore: (id, vacancyId = null) => {
        const qs = vacancyId ? `?vacancy_id=${encodeURIComponent(vacancyId)}` : ''
        return apiFetch(`/candidates/${id}/ai-match${qs}`)
    },
    regenerateMatchScore: (id, vacancyId = null) => {
        const qs = vacancyId ? `?vacancy_id=${encodeURIComponent(vacancyId)}` : ''
        return apiFetch(`/candidates/${id}/ai-match${qs}`, { method: 'POST' })
    },
    getMatchScore: (id, vacancyId = null) => {
        const qs = vacancyId ? `?vacancy_id=${encodeURIComponent(vacancyId)}` : ''
        return apiFetch(`/candidates/${id}/ai-match${qs}`, { method: 'POST' })
    },
    getCachedInterviewQuestions: (id, vacancyId = null) => {
        const qs = vacancyId ? `?vacancy_id=${encodeURIComponent(vacancyId)}` : ''
        return apiFetch(`/candidates/${id}/interview-questions${qs}`)
    },
    getInterviewQuestions: (id, vacancyId = null) => {
        const qs = vacancyId ? `?vacancy_id=${encodeURIComponent(vacancyId)}` : ''
        return apiFetch(`/candidates/${id}/interview-questions${qs}`, { method: 'POST' })
    },
    importFile: (file, opts = {}) => {
        // Backwards-compat: prior signature was importFile(file, vacancyTitleString).
        // Now opts can be a string (legacy) or { vacancyId, vacancyTitle, forceNew, mergeIntoId }.
        const norm = typeof opts === 'string'
            ? { vacancyTitle: opts, vacancyId: null }
            : (opts || {})
        const { vacancyId, vacancyTitle, forceNew, mergeIntoId } = norm
        const params = new URLSearchParams()
        if (vacancyId) params.set('vacancy_id', vacancyId)
        if (vacancyTitle) params.set('vacancy_title', vacancyTitle)
        if (forceNew) params.set('force_new', 'true')
        if (mergeIntoId) params.set('merge_into_id', mergeIntoId)
        const qs = params.toString() ? `?${params.toString()}` : ''
        const formData = new FormData()
        formData.append('file', file)
        return apiFetch(`/candidates/import-file${qs}`, {
            method: 'POST',
            body: formData,
        })
    },
    importText: ({
        resume_text,
        vacancy_id = null,
        source = null,
        vacancy_title = null,
        force_new = false,
        merge_into_id = null,
    } = {}) =>
        apiFetch('/candidates/import-text', {
            method: 'POST',
            body: JSON.stringify({
                resume_text, vacancy_id, source, vacancy_title, force_new, merge_into_id,
            }),
        }),
    // BL Суперкнопка PR3: pre-flight strict-3-of-3 lookup before create.
    preflightDedup: ({ full_name = null, email = null, phone = null } = {}) =>
        apiFetch('/candidates/preflight-dedup', {
            method: 'POST',
            body: JSON.stringify({ full_name, email, phone }),
        }),
    parseResumeText: ({ resume_text, source = null } = {}) =>
        apiFetch('/candidates/parse-resume-text', {
            method: 'POST',
            body: JSON.stringify({ resume_text, source }),
        }),
    // BL Суперкнопка follow-up: extract+parse a file WITHOUT writing — used
    // by file-mode preflight (PDF/DOCX → AI → preflight-dedup → dialog or
    // import-file with force_new/merge_into_id).
    parseResumeFile: (file) => {
        const formData = new FormData()
        formData.append('file', file)
        return apiFetch('/candidates/parse-resume-file', {
            method: 'POST',
            body: formData,
        })
    },
    importFileForCandidate: (id, file) => {
        const formData = new FormData()
        formData.append('file', file)
        return apiFetch(`/candidates/${id}/import-file`, {
            method: 'POST',
            body: formData,
        })
    },
    updateVisibleDepartments: (id, departmentIds) =>
        apiFetch(`/candidates/${id}/visible-departments`, {
            method: 'PATCH',
            body: JSON.stringify({ department_ids: departmentIds }),
        }),
    listApplications: (id) => apiFetch(`/candidates/${id}/applications`),
    createApplication: (id, data) => apiFetch(`/candidates/${id}/applications`, {
        method: 'POST',
        body: JSON.stringify(data),
    }),
    downloadCsv: async () => {
        const response = await apiFetchRaw('/candidates/export/csv')
        const blob = await response.blob()
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `candidates_export_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
    },
}